
import React from "react";
import { Episode, Guest, getStatusColor, getStatusLabel } from "@/data/mockData";
import { CalendarIcon, User, Headphones, MessageSquare, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";

interface ShowListProps {
  episodes: Episode[];
  guests: Guest[];
}

const ShowList: React.FC<ShowListProps> = ({ episodes, guests }) => {
  const getGuestName = (guestId: string) => {
    const guest = guests.find((g) => g.id === guestId);
    return guest ? guest.name : "Unknown Guest";
  };

  const truncate = (text: string, maxLength: number) => {
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not scheduled";
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (error) {
      return "Invalid date";
    }
  };

  if (episodes.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 rounded-full bg-metsci-blue/10 flex items-center justify-center mb-4">
          <MessageSquare className="h-8 w-8 text-metsci-blue" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No episodes found</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          No episodes match your current filters. Try adjusting your search or filters.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 animate-slide-up">
      {episodes.map((episode) => (
        <Card key={episode.id} className="overflow-hidden hover:shadow-md transition-shadow border-metsci-blue/10">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-48 lg:w-64 h-48 md:h-full relative">
                <img 
                  src={episode.thumbnailUrl} 
                  alt={episode.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent md:bg-gradient-to-r flex items-end p-4 md:items-center">
                  <Badge className={`${getStatusColor(episode.status)} text-white`}>
                    {getStatusLabel(episode.status)}
                  </Badge>
                </div>
              </div>
              
              <div className="flex-1 p-4 md:p-6">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-metsci-primary mb-1">{episode.title}</h3>
                    <div className="flex items-center text-sm text-muted-foreground mb-3">
                      <span className="flex items-center mr-4">
                        <User className="h-3.5 w-3.5 mr-1.5 text-metsci-blue" />
                        {getGuestName(episode.guestId)}
                      </span>
                      <span className="flex items-center mr-4">
                        <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-metsci-blue" />
                        {formatDate(episode.publishDate || episode.recordingDate)}
                      </span>
                      {episode.duration && (
                        <span className="flex items-center">
                          <Clock className="h-3.5 w-3.5 mr-1.5 text-metsci-blue" />
                          {episode.duration}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      {truncate(episode.description, 160)}
                    </p>
                    
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {episode.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="bg-metsci-white text-metsci-primary border-metsci-primary/20">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {episode.status === "published" && (
                    <div className="flex items-center justify-center md:justify-end">
                      <div className="text-center">
                        <div className="flex items-center justify-center h-14 w-14 md:h-16 md:w-16 rounded-full bg-metsci-blue/10 mx-auto mb-1">
                          <Headphones className="h-6 w-6 text-metsci-blue" />
                        </div>
                        <div className="text-lg font-semibold text-metsci-primary">
                          {Math.floor(Math.random() * 10000)}
                        </div>
                        <div className="text-xs text-muted-foreground">Listens</div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="outline" size="sm" className="border-metsci-blue/20 text-metsci-blue hover:bg-metsci-blue/10">
                    View Details
                  </Button>
                  <Button size="sm" className="bg-metsci-primary hover:bg-metsci-red text-white">
                    {episode.status === "published" ? "Promote" : 
                     episode.status === "completed" ? "Publish" : 
                     episode.status === "scheduled" ? "Record" : "Edit"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ShowList;
